import type { OrderItem, Table } from '../types';
import type { UberDoordashPlatform } from '../components/pos/UberDoordashOrderModal';

export const WAITSTAFF_ORDER_DRAFT_KEY = 'kingspark_waitstaff_order_draft';

export type WaitstaffDraftOrderType = 'dine-in' | 'takeaway' | 'delivery' | 'uber-doordash';

export type WaitstaffOrderDraft = {
  staffId: string;
  items: OrderItem[];
  notes: string;
  orderType: WaitstaffDraftOrderType | null;
  tableId?: number;
  tableName?: string;
  deliveryCustomerName?: string;
  uberDoordashDetails?: { platform: UberDoordashPlatform; customerName: string } | null;
  orderToUpdateId?: string;
  savedAt: number;
};

export function waitstaffDraftStorageKey(staffId: string): string {
  const id = String(staffId || '').trim() || 'anon';
  return `${WAITSTAFF_ORDER_DRAFT_KEY}:${id}`;
}

export function waitstaffDraftHasWork(draft: WaitstaffOrderDraft | null | undefined): boolean {
  if (!draft) return false;
  const hasItems = (draft.items ?? []).some((item) => !item.removed && (item.quantity ?? 0) > 0);
  if (hasItems) return true;
  if (draft.orderToUpdateId) return true;
  if (draft.tableId != null) return true;
  if (draft.orderType === 'takeaway' || draft.orderType === 'delivery' || draft.orderType === 'uber-doordash') {
    return true;
  }
  return false;
}

export function waitstaffDraftItemCount(draft: WaitstaffOrderDraft | null | undefined): number {
  if (!draft) return 0;
  return (draft.items ?? [])
    .filter((item) => !item.removed)
    .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
}

export function waitstaffDraftSummary(draft: WaitstaffOrderDraft | null | undefined): string {
  if (!waitstaffDraftHasWork(draft) || !draft) return '';
  const count = waitstaffDraftItemCount(draft);
  const itemBit = count === 1 ? '1 item' : `${count} items`;
  if (draft.orderToUpdateId) return `Running order · ${itemBit}`;
  if (draft.orderType === 'dine-in') {
    const table = draft.tableName || (draft.tableId != null ? `Table ${draft.tableId}` : 'Dine-in');
    return `${table} · ${itemBit}`;
  }
  if (draft.orderType === 'uber-doordash') {
    const platform = draft.uberDoordashDetails?.platform || 'Uber / DoorDash';
    return `${platform} · ${itemBit}`;
  }
  if (draft.orderType === 'delivery') return `Delivery · ${itemBit}`;
  if (draft.orderType === 'takeaway') return `Takeaway · ${itemBit}`;
  return itemBit;
}

export function buildWaitstaffOrderDraft(input: {
  staffId: string;
  items: OrderItem[];
  notes: string;
  orderType: WaitstaffDraftOrderType | null;
  table: Table | null;
  deliveryCustomerName: string;
  uberDoordashDetails: WaitstaffOrderDraft['uberDoordashDetails'];
  orderToUpdateId?: string;
  savedAt?: number;
}): WaitstaffOrderDraft {
  return {
    staffId: input.staffId,
    items: input.items.map((item) => ({ ...item })),
    notes: input.notes || '',
    orderType: input.orderType,
    tableId: input.table != null ? Number(input.table.id) : undefined,
    tableName: input.table?.name,
    deliveryCustomerName: input.deliveryCustomerName || '',
    uberDoordashDetails: input.uberDoordashDetails ?? null,
    orderToUpdateId: input.orderToUpdateId,
    savedAt: input.savedAt ?? Date.now(),
  };
}

export function readWaitstaffOrderDraft(staffId: string): WaitstaffOrderDraft | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(waitstaffDraftStorageKey(staffId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WaitstaffOrderDraft;
    if (!parsed || !Array.isArray(parsed.items)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeWaitstaffOrderDraft(draft: WaitstaffOrderDraft): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (!waitstaffDraftHasWork(draft)) {
      clearWaitstaffOrderDraft(draft.staffId);
      return;
    }
    localStorage.setItem(waitstaffDraftStorageKey(draft.staffId), JSON.stringify(draft));
  } catch {
    // ignore quota / private mode
  }
}

export function clearWaitstaffOrderDraft(staffId: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(waitstaffDraftStorageKey(staffId));
  } catch {
    // ignore
  }
}
